import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';

function CartPaymentModal({
  totalKRW,
  itemCount,
  onClose,
  onKRWPay,
  onUSDTPay,
}: {
  totalKRW: number;
  itemCount: number;
  onClose: () => void;
  onKRWPay: () => void;
  onUSDTPay: () => void;
}) {
  const formatKRW = (n: number) => n.toLocaleString('ko-KR');
  const usdtEstimate = (totalKRW / 1370).toFixed(2);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-sm sm:mx-4 rounded-t-3xl sm:rounded-2xl overflow-hidden">
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
          aria-label="닫기"
        >
          <i className="ri-close-line text-gray-600 text-base w-4 h-4 flex items-center justify-center" />
        </button>

        <div className="px-5 pt-5 pb-6">
          {/* 장바구니 요약 */}
          <div className="flex items-center gap-3 mb-5 pr-8">
            <div className="w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-100 flex-shrink-0 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line text-emerald-600 text-2xl w-6 h-6 flex items-center justify-center" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 mb-0.5">결제 방법 선택</p>
              <h3 className="text-sm font-bold text-gray-900 leading-snug">장바구니 전체 상품</h3>
              <p className="text-xs text-gray-500 mt-0.5">총 {itemCount}가지 상품</p>
            </div>
          </div>

          {/* 가격 요약 */}
          <div className="bg-gray-50 rounded-xl p-3 mb-5 flex items-center justify-between">
            <span className="text-sm text-gray-500">총 결제금액</span>
            <div className="text-right">
              <p className="text-base font-black text-gray-900">{formatKRW(totalKRW)}원</p>
              <p className="text-xs font-bold text-teal-600">≈ USDT ${usdtEstimate}</p>
            </div>
          </div>

          <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
            결제 방법을 선택하세요
          </p>

          <div className="space-y-3">
            {/* 원화(KRW) 결제 */}
            <button
              type="button"
              onClick={onKRWPay}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100 transition-all cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-emerald-600 rounded-xl flex-shrink-0 group-hover:bg-emerald-700 transition-colors">
                <i className="ri-won-line text-white text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-gray-900">원화(KRW) 결제</p>
                <p className="text-xs text-gray-500 mt-0.5">카드 · 계좌이체 · 카카오페이 · 네이버페이</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1 text-emerald-600">
                <span className="text-sm font-black">{formatKRW(totalKRW)}원</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </div>
            </button>

            {/* USDT 결제 */}
            <button
              type="button"
              onClick={onUSDTPay}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-teal-200 bg-teal-50 hover:border-teal-400 hover:bg-teal-100 transition-all cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-teal-500 rounded-xl flex-shrink-0 group-hover:bg-teal-600 transition-colors">
                <i className="ri-bit-coin-line text-white text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  USDT 결제
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-teal-500 text-white text-[10px] font-black">
                    ERC-20
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">암호화폐 · 지갑 주소로 직접 전송</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1 text-teal-600">
                <span className="text-sm font-black">${usdtEstimate}</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </div>
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-4 leading-relaxed">
            USDT 결제는 ERC-20 네트워크 전용입니다<br />
            TX Hash 제출 후 관리자 확인을 거쳐 처리됩니다
          </p>
        </div>
      </div>
    </div>
  );
}

export default function CartPage() {
  const { items, updateQuantity, removeFromCart, getTotalPrice } = useCart();
  const navigate = useNavigate();
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  const handleKRWPay = () => {
    setShowPaymentModal(false);
    navigate('/order');
  };

  const handleUSDTPay = () => {
    setShowPaymentModal(false);
    navigate('/point-purchase');
  };

  if (items.length === 0) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 pt-20 pb-16">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center py-16 md:py-20">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-5 flex items-center justify-center bg-gray-100 rounded-full">
                <i className="ri-shopping-cart-line text-3xl md:text-4xl text-gray-400 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                장바구니가 비어있습니다
              </h2>
              <p className="text-sm md:text-base text-gray-600 mb-8">원하시는 제품을 담아보세요!</p>
              <Link
                to="/products"
                className="inline-block px-7 py-3 bg-emerald-600 text-white rounded-full font-semibold text-sm md:text-base hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                제품 둘러보기
              </Link>
            </div>
          </div>
        </div>
        <PageFooter />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8" style={{ fontFamily: 'Noto Serif KR, serif' }}>
            장바구니
          </h1>

          <div className="grid lg:grid-cols-3 gap-5 md:gap-8">
            {/* 장바구니 아이템 목록 */}
            <div className="lg:col-span-2 space-y-3 md:space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex gap-3 md:gap-5">
                    <div className="w-20 h-20 md:w-28 md:h-28 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2 mb-2 md:mb-3">
                        <div className="min-w-0">
                          <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-0.5 leading-snug">{item.name}</h3>
                          <p className="text-xs md:text-sm text-gray-500">{item.category}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                          aria-label="삭제"
                        >
                          <i className="ri-close-line text-lg md:text-xl w-4 h-4 md:w-5 md:h-5 flex items-center justify-center"></i>
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1 md:gap-2 bg-gray-50 rounded-lg p-0.5 md:p-1">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-600 hover:text-emerald-600 transition-colors cursor-pointer"
                            aria-label="수량 감소"
                          >
                            <i className="ri-subtract-line text-base md:text-lg w-4 h-4 flex items-center justify-center"></i>
                          </button>
                          <span className="w-7 md:w-10 text-center font-semibold text-gray-900 text-sm md:text-base">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-600 hover:text-emerald-600 transition-colors cursor-pointer"
                            aria-label="수량 증가"
                          >
                            <i className="ri-add-line text-base md:text-lg w-4 h-4 flex items-center justify-center"></i>
                          </button>
                        </div>
                        <div className="text-right">
                          {item.originalPrice && (
                            <p className="text-xs text-gray-400 line-through mb-0.5">{formatPrice(item.originalPrice * item.quantity)}원</p>
                          )}
                          <p className="text-base md:text-xl font-bold text-emerald-600">{formatPrice(item.price * item.quantity)}원</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 주문 요약 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-5 md:p-6 shadow-sm lg:sticky lg:top-24">
                <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-5 md:mb-6" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  주문 요약
                </h2>

                <div className="space-y-3 md:space-y-4 mb-5 md:mb-6 pb-5 md:pb-6 border-b border-gray-100">
                  <div className="flex justify-between text-sm md:text-base text-gray-600">
                    <span>상품 금액</span>
                    <span>{formatPrice(getTotalPrice())}원</span>
                  </div>
                  <div className="flex justify-between text-sm md:text-base text-gray-600">
                    <span>배송비</span>
                    <span className="text-emerald-600 font-semibold">무료</span>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-5 md:mb-6">
                  <span className="text-base md:text-lg font-bold text-gray-900">총 결제금액</span>
                  <span className="text-xl md:text-2xl font-bold text-emerald-600">{formatPrice(getTotalPrice())}원</span>
                </div>

                {/* 결제 방법 선택 버튼 2개 */}
                <div className="space-y-2.5 mb-3">
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full py-3.5 md:py-4 bg-emerald-600 text-white rounded-full font-bold text-base md:text-lg hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-secure-payment-line w-5 h-5 flex items-center justify-center" />
                    결제 방법 선택
                  </button>
                  <div className="flex items-center justify-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-won-line w-3 h-3 flex items-center justify-center text-emerald-500" />
                      원화(KRW)
                    </span>
                    <span className="text-gray-200">|</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-bit-coin-line w-3 h-3 flex items-center justify-center text-teal-500" />
                      USDT
                    </span>
                  </div>
                </div>

                <Link
                  to="/products"
                  className="block w-full py-3 text-center border-2 border-gray-200 text-gray-700 rounded-full font-semibold text-sm md:text-base hover:border-emerald-600 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  계속 쇼핑하기
                </Link>

                <Link
                  to="/order-history"
                  className="block w-full py-3 text-center text-gray-500 text-sm hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center"></i>
                  주문 내역 확인하기
                </Link>

                <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-gray-100 space-y-2.5 md:space-y-3">
                  <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                    <i className="ri-shield-check-line text-emerald-600 w-4 h-4 flex items-center justify-center"></i>
                    <span>안전한 결제 시스템</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                    <i className="ri-truck-line text-emerald-600 w-4 h-4 flex items-center justify-center"></i>
                    <span>전국 무료 배송</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                    <i className="ri-refresh-line text-emerald-600 w-4 h-4 flex items-center justify-center"></i>
                    <span>30일 이내 무료 반품</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PageFooter />

      {showPaymentModal && (
        <CartPaymentModal
          totalKRW={getTotalPrice()}
          itemCount={items.length}
          onClose={() => setShowPaymentModal(false)}
          onKRWPay={handleKRWPay}
          onUSDTPay={handleUSDTPay}
        />
      )}
    </>
  );
}
